import { useDispatch, useSelector } from 'react-redux';
import {
	getAdditionalServicesData,
	getAdditionalServicesLoading,
} from 'redux/reducers/emManagement/selectors';
import { useEffect, useState } from 'react';
import {
	createAdditionalServiceRequest,
	getAdditionalServicesRequest,
	updateAdditionalServiceRequest,
} from 'redux/reducers/emManagement/reducer';
import { PAGE_SIZE } from 'services/constants/env';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { Box, Button, Typography } from '@mui/material';
import { TAdditionalService } from 'redux/reducers/emManagement/types';
import { TTableStructure } from 'ui/EasTable/types';
import { TBoxStructure } from 'components/EMManagement/EMManagementId/EasParamsBox/types';
import { EditParamsDialog } from './EditParamsDialog';
import { tableStructure } from './tableStructure';

export const AdditionalServices = () => {
	const dispatch = useDispatch();
	const additionalServices = useSelector(getAdditionalServicesData);
	const additionalServicesLoading = useSelector(getAdditionalServicesLoading);
	useEffect(() => {
		dispatch(getAdditionalServicesRequest({ pageNumber: 1, pageSize: PAGE_SIZE }));
	}, [dispatch]);

	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};

	const [idToEdit, setIdToEdit] = useState<number | null>(null);

	const handleSave = (data: Record<string, any>) => {
		const isRecurring = Boolean(data.isRecurring);
		const servicePeriod = isRecurring ? Number(data.servicePeriod) : undefined;
		const getAdditionalService = {
			pageNumber: 1,
			pageSize: PAGE_SIZE,
			filters: [],
			searchString: '',
			orderBy: [],
		};

		if (idToEdit === null) {
			dispatch(
				createAdditionalServiceRequest({
					data: {
						name: data.name,
						price: Number(data.price),
						isRecurring,
						servicePeriod,
					},
					getAdditionalService,
				}),
			);
		} else {
			dispatch(
				updateAdditionalServiceRequest({
					id: idToEdit,
					data: {
						name: data.name,
						price: Number(data.price),
						isRecurring,
						servicePeriod,
					},
					getAdditionalService,
				}),
			);
		}
		handleEditClose();
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		switch (typeOfDialog) {
			case 'Edit':
				setIdToEdit(id);
				setIsEditDialogOpen(true);
				break;
			default:
				break;
		}
	};

	const tableStructureToBoxStructure = (
		tableStruc: TTableStructure<keyof TAdditionalService>[],
	) => {
		const boxStructure: TBoxStructure<TAdditionalService> = {
			body: [],
			header: [],
			title: idToEdit ? 'Edit Additional Service' : 'Add Additional Service',
		};
		tableStruc.forEach((item: TTableStructure<keyof TAdditionalService>) => {
			if (item?.editable === true && item.selector !== 'actions') {
				boxStructure.body.push({
					name: item.name,
					selector: item.selector,
					type: item.type,
					editable: true,
				});
			}
		});
		return boxStructure;
	};

	return (
		<Box sx={{ margin: '5px' }}>
			<EasTableWithoutFilter
				tableStructure={tableStructure}
				items={additionalServices}
				itemsIsLoading={additionalServicesLoading}
				handleAction={handleAction}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / <b>Additional Services</b>
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() => {
							setIdToEdit(null);
							setIsEditDialogOpen(true);
						}}
					>
						Add Service
					</Button>
				</Box>
			</EasTableWithoutFilter>
			<EditParamsDialog<TAdditionalService>
				open={isEditDialogOpen}
				setClose={handleEditClose}
				data={additionalServices?.data?.find((item) => item.id === idToEdit) ?? null}
				boxStructure={tableStructureToBoxStructure(tableStructure)}
				additionalServices={additionalServices?.data ?? null}
				title={idToEdit ? 'Edit Additional Service' : 'Add Additional Service'}
				onSave={handleSave}
			/>
		</Box>
	);
};
